'use client';

import type { ComponentProps } from 'react';

import { Checkbox as CheckboxPrimitive } from '@base-ui-components/react/checkbox';
import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { CheckIcon } from 'lucide-react';

/**
 * The box a checkbox paints.
 *
 * `base` is the form control: large, softly rounded, focused with the brand's
 * own ring. `compact` is the denser one a long list of options is built from —
 * a smaller square with a neutral focus ring, so a column of them reads as a
 * list of choices rather than as a column of form fields.
 *
 * The base holds what a checkbox is in either appearance — a bordered square
 * that marks itself when checked, rings when focused and dims when disabled.
 *
 * `inline-flex align-middle` is load-bearing, not decoration. Base UI renders
 * `Checkbox.Root` as a `<span>`, and `width` / `height` do not apply to a
 * non-replaced inline box — a checkbox that declares no display collapses to
 * its two side borders, a 2px bar the height of the line it sits on, wherever
 * the surrounding layout does not blockify it (an absolutely positioned or
 * flex-item call site happens to escape). The centring on the same rule is what
 * puts the tick in the middle of the box rather than at its top-left.
 *
 * `align-middle` is the second half. An inline-level box aligned on the
 * baseline takes that baseline from its own content, and this box's content —
 * the indicator — exists only while checked: baseline-aligned, the box sits 3px
 * lower once ticked and drags the height of the line with it, so a toggle
 * reflows the row it sits in. `middle` measures against the text's x-height
 * instead, which the checked state cannot change. It is inert wherever the
 * surrounding layout already blockified the root, so it costs those call sites
 * nothing.
 *
 * The resting outline is `border-foreground`, deliberately not
 * `border-primary-foreground`: that token means "content that sits ON the
 * primary colour", and an unfilled box sits on the page. Borrowing it renders
 * correctly only in a brand whose primary is light enough to take dark content
 * — in one whose primary is dark, the token is white and the box disappears
 * against the page.
 * An arm names only what it changes: its size, whether the corners round, and
 * which colour its focus ring takes.
 */
export const checkboxVariants = cva(
  [
    'peer border-foreground inline-flex shrink-0 items-center justify-center border align-middle outline-none',
    'data-[checked]:border-primary data-[checked]:bg-primary data-[checked]:text-primary-foreground',
    'focus-visible:ring-[2px]',
    'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        base: ['size-6 rounded-sm', 'focus-visible:ring-primary/60'],
        compact: [
          'size-5 transition-shadow',
          'focus-visible:border-foreground focus-visible:ring-foreground/60',
        ],
      },
    },
    defaultVariants: { variant: 'base' },
  }
);

/** The tick, sized to the box it sits in. */
const checkboxIconVariants = cva('', {
  variants: {
    variant: {
      base: 'size-4.5',
      compact: 'size-4',
    },
  },
  defaultVariants: { variant: 'base' },
});

export type CheckboxProps = ComponentProps<typeof CheckboxPrimitive.Root> &
  VariantProps<typeof checkboxVariants>;

export const Checkbox = ({ className, variant, ...props }: CheckboxProps) => (
  <CheckboxPrimitive.Root
    data-slot="checkbox"
    className={clsxm(checkboxVariants({ variant }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      data-slot="checkbox-indicator"
      className="flex items-center justify-center text-current transition-none"
    >
      <CheckIcon className={checkboxIconVariants({ variant })} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);

/**
 * A checkbox whose selectable value IS a colour: a disc filled with it, ringed
 * once chosen. A primitive of its own rather than an arm of {@link Checkbox},
 * because it renders a different tree — the colour is a nested element, not a
 * class — and because its tick has to stay legible against whatever colour it
 * was handed.
 */
export const checkboxSwatchVariants = cva(
  [
    'peer border-foreground/20 size-10 shrink-0 rounded-full border outline-none',
    'data-[checked]:border-foreground',
    'focus-visible:border-foreground focus-visible:ring-foreground/60 focus-visible:ring-[2px]',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'flex items-center justify-center',
  ],
  {
    variants: {
      /**
       * How light the swatch reads, which decides the tick drawn over it: a
       * pale colour takes the dark mark, everything else the light one. Stated
       * as the swatch's own tone rather than as the list of names one
       * catalogue happens to give its pale colours.
       */
      tone: {
        light: 'data-[checked]:text-foreground',
        dark: 'data-[checked]:text-foreground-inverted',
      },
    },
    defaultVariants: { tone: 'dark' },
  }
);

/** The filled disc inside the ring. */
const checkboxSwatchColorClasses =
  'flex size-8 items-center justify-center rounded-full text-current transition-none';

export type CheckboxSwatchProps = ComponentProps<typeof CheckboxPrimitive.Root> &
  VariantProps<typeof checkboxSwatchVariants> & {
    /**
     * The colour to fill the disc with, as a final CSS value. Resolving a
     * catalogue's colour vocabulary to one is the caller's: a primitive that
     * knew that vocabulary would carry one tenant's palette for every tenant.
     */
    readonly color: string;
  };

export const CheckboxSwatch = ({ className, color, tone, ...props }: CheckboxSwatchProps) => (
  <CheckboxPrimitive.Root
    data-slot="checkbox-swatch"
    className={clsxm(checkboxSwatchVariants({ tone }), className)}
    {...props}
  >
    <div
      data-slot="checkbox-swatch-color"
      className={checkboxSwatchColorClasses}
      style={{ background: color }}
    >
      <CheckboxPrimitive.Indicator data-slot="checkbox-indicator">
        <CheckIcon className="size-4" />
      </CheckboxPrimitive.Indicator>
    </div>
  </CheckboxPrimitive.Root>
);
